<script setup lang="ts">
import type { NuxtError } from '#app';
import { computed } from 'vue';

const props = defineProps({
  error: {
    type: Object as () => NuxtError,
    required: true,
  },
});

const { t } = useI18n();

const is404 = computed(() => props.error?.statusCode === 404);

const errorTitle = computed(() => {
  if (is404.value) {
    return t('error.pageNotFound');
  }
  return t('error.somethingWentWrong');
});

const errorDesc = computed(() => {
  if (is404.value) {
    return t('error.notFoundDesc');
  }
  return props.error?.message || t('error.genericDesc');
});

const actionText = computed(() => {
  return t('error.goHome');
});

const handleClearError = () => {
  clearError({ redirect: '/' });
};
</script>

<template>
  <div
    class="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[#0b1329] px-4 font-sans text-white selection:bg-rose-500 selection:text-white"
  >
    <!-- Ambient Neon Background Orbs -->
    <div
      class="pointer-events-none absolute -top-1/4 -left-1/4 h-[600px] w-[600px] animate-pulse rounded-full bg-indigo-500/10 blur-[120px]"
    ></div>
    <div
      class="pointer-events-none absolute -right-1/4 -bottom-1/4 h-[600px] w-[600px] animate-pulse rounded-full bg-rose-500/10 blur-[120px]"
      style="animation-duration: 6s"
    ></div>

    <!-- Decorative Grid Overlay -->
    <div
      class="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:4rem_4rem]"
    ></div>

    <div class="relative z-10 w-full max-w-xl text-center">
      <!-- Animated Visual Representation of 404 / Error -->
      <div class="relative mb-8 select-none">
        <div class="absolute inset-0 flex items-center justify-center opacity-30 blur-2xl">
          <span class="text-[10rem] font-extrabold tracking-widest text-indigo-500 md:text-[14rem]">
            {{ error.statusCode || 500 }}
          </span>
        </div>

        <h1
          class="animate-pulse bg-gradient-to-r from-indigo-400 via-purple-400 to-rose-400 bg-clip-text font-mono text-[8rem] leading-none font-black tracking-widest text-transparent drop-shadow-sm md:text-[12rem]"
        >
          {{ error.statusCode || 500 }}
        </h1>
      </div>

      <!-- Glassmorphic Content Card -->
      <div
        class="relative overflow-hidden rounded-3xl border border-white/[0.08] bg-white/[0.03] p-8 shadow-2xl backdrop-blur-xl transition-all duration-300 hover:border-white/[0.12] hover:bg-white/[0.04] md:p-12"
      >
        <!-- Top accent border glow -->
        <div
          class="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-indigo-500/50 to-transparent"
        ></div>

        <!-- Warning Icon / Error Indicator -->
        <div
          class="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-2xl border border-indigo-500/30 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 text-indigo-400 shadow-inner"
        >
          <Icon name="lucide:alert-circle" class="h-8 w-8" />
        </div>

        <!-- Headline -->
        <h2 class="mb-4 text-2xl font-bold tracking-tight text-white/95 md:text-3xl">
          {{ errorTitle }}
        </h2>

        <!-- Description -->
        <p class="mb-8 font-sans text-base leading-relaxed font-light text-slate-400 md:text-lg">
          {{ errorDesc }}
        </p>

        <!-- Recovery Button -->
        <button
          class="group relative inline-flex w-full transform items-center justify-center gap-2 overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 to-rose-600 px-8 py-3.5 text-base font-medium text-white shadow-lg shadow-indigo-600/20 transition-all duration-300 hover:-translate-y-0.5 hover:from-indigo-500 hover:to-rose-500 hover:shadow-indigo-600/30 active:translate-y-0 sm:w-auto"
          @click="handleClearError"
        >
          <Icon name="lucide:home" class="h-5 w-5 transition-transform group-hover:scale-110" />
          <span>{{ actionText }}</span>

          <!-- Inner Glow Light Effect -->
          <div
            class="absolute inset-0 h-full w-full -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-1000 group-hover:translate-x-full"
          ></div>
        </button>
      </div>

      <!-- Quick back links / footer decoration -->
      <p class="mt-8 font-mono text-xs tracking-widest text-slate-500 uppercase">
        E-School Cambodia &copy; {{ new Date().getFullYear() }}
      </p>
    </div>
  </div>
</template>

<style scoped>
:deep(body) {
  background-color: #0b1329;
}
</style>
